import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { QuantityAndUnitInput } from '@/shared/inputs/QuantityAndUnitInput.tsx';
import { PriceAndTaxClassInput } from '@/shared/inputs/PriceAndTaxClassInput.tsx';
import { NegativeSalesPriceHint, ZeroSalesPriceHint } from '@/shared/components/PriceInputHints.tsx';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { usePositionFormQuantities } from '@/shared/forms/AutomaticProductsForm/hooks/usePositionFormQuantities.ts';

export function PositionSalesTransportFieldset() {
    const { t } = useTranslation();
    const { taxClassOptions, customerOrganization, formRules } = useAutomaticFormContext();

    const methods = useFormContext<AutomaticProductsFormValues>();
    const { watch, setValue, register } = methods;
    const { onSalesTransportUnitChange, onSalesTransportQuantityChange } = usePositionFormQuantities(methods);

    const [salesTransportPrice, salesTransportUnit, currencyCode, showZeroSalesTransportPrice, isStandalone] = watch([
        'salesTransportPrice',
        'salesTransportUnit',
        'currencyCode',
        'showZeroSalesTransportPrice',
        'isStandalone',
    ]);

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.transport')}
            dataTest="position-sales-transport-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-2 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn className="@2xl:col-span-5">
                    <div className="grid grid-cols-2 gap-1">
                        <FieldsetLabel>{t('forms.automaticProductsForm.labels.quantity')}</FieldsetLabel>
                        <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                    </div>
                    <QuantityAndUnitInput
                        quantityProps={{
                            ...register('salesTransportQuantity'),
                            disabled: !isStandalone,
                            onQuantityChange: qty => {
                                if (isStandalone) {
                                    onSalesTransportQuantityChange(qty);
                                }
                            },
                        }}
                        unitProps={{
                            ...register('salesTransportUnit'),
                            supportedUnitNames: [...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options],
                            variant: 'lg',
                            disabled: formRules?.salesTransportUnit?.disabled,
                            onUnitChange: unit => onSalesTransportUnitChange(toAutomaticUnit(unit)),
                        }}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="@2xl:col-span-3">
                    <div className="grid grid-cols-3 gap-1">
                        <FieldsetLabel className="col-span-2">
                            {t('forms.automaticProductsForm.labels.unitPrice')}
                        </FieldsetLabel>
                        <FieldsetLabel className="col-span-1">
                            {t('forms.automaticProductsForm.labels.taxClass')}
                        </FieldsetLabel>
                    </div>
                    <PriceAndTaxClassInput
                        priceProps={{
                            ...register('salesTransportPrice'),
                            unit: salesTransportUnit,
                            currencyCode,
                            testId: 'sales-transport-price-input',
                        }}
                        taxClassProps={{
                            ...register('salesTransportTaxClassId'),
                            options: taxClassOptions,
                            testId: 'sales-transport-tax-class-input',
                            disabled: formRules?.salesTransportTaxClassId?.disabled,
                        }}
                    />
                </FieldsetColumn>

                {salesTransportPrice === 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroSalesPriceHint
                            orderingOrganizationName={customerOrganization.name}
                            isBillingRelevant={showZeroSalesTransportPrice ?? false}
                            onClick={() =>
                                setValue('showZeroSalesTransportPrice', !(showZeroSalesTransportPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(salesTransportPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativeSalesPriceHint orderingOrganizationName={customerOrganization.name} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
